import is from '@sindresorhus/is';
import { type FunctionComponent, type KeyboardEvent, type ReactNode, useState } from 'react';

import { Button } from '~/ui/core/button/Button';
import { Input } from '~/ui/core/input/Input';
import { AssistantBubble } from '~/ui/domain/chat/AssistantBubble';
import { ChangeProposalCard } from '~/ui/domain/chat/ChangeProposalCard';
import type { ChangeProposalStatus } from '~/ui/domain/chat/types';

import {
    type ChangeProposal,
    type ChangeProposalResponseEvent,
    PRESENT_CHANGE_PROPOSAL_TOOL_ID,
} from '../../data-contract/chat';
import { useSubmitChatInteractionCallback } from '../api';
import type { StructuredRenderer } from './types';

type Props = {
    readonly event: ChangeProposalResponseEvent;
    readonly interactive: boolean;
};

type ProposalFooterActionsProps = {
    readonly callbackPending: boolean;
    readonly draftSuggestion: string;
    readonly interactive: boolean;
    readonly onAccept: () => void;
    readonly onDecline: () => void;
    readonly onDraftSuggestionChange: (value: string) => void;
    readonly onSuggest: (value: string) => void;
    readonly status: ChangeProposalStatus;
    readonly suggestion?: string;
};

const submitSuggestionFromKey = (
    event: KeyboardEvent<HTMLInputElement>,
    draftSuggestion: string,
    onSuggest: (value: string) => void,
): void => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const value = draftSuggestion.trim();
    if (value.length > 0) onSuggest(value);
};

const ProposalFooterActions: FunctionComponent<ProposalFooterActionsProps> = ({
    callbackPending,
    draftSuggestion,
    interactive,
    onAccept,
    onDecline,
    onDraftSuggestionChange,
    onSuggest,
    status,
    suggestion,
}) => {
    if (status === 'accepted') {
        return (
            <ChangeProposalCard.Actions>
                <Button className="w-full bg-green-700 text-white-200" disabled={true} size="sm">
                    Accepted
                </Button>
            </ChangeProposalCard.Actions>
        );
    }

    if (status === 'declined') {
        return (
            <ChangeProposalCard.Actions>
                <Button className="w-full" disabled={true} size="sm">
                    Declined
                </Button>
            </ChangeProposalCard.Actions>
        );
    }

    if (status === 'suggested') {
        return (
            <ChangeProposalCard.Actions>
                <span className="truncate text-sm text-black-700">{suggestion ?? draftSuggestion}</span>
            </ChangeProposalCard.Actions>
        );
    }

    const canInteract = interactive && !callbackPending;

    return (
        <ChangeProposalCard.Actions>
            <Button disabled={!canInteract} size="sm" onClick={onAccept}>
                Accept
            </Button>
            <Button disabled={!canInteract} size="sm" onClick={onDecline}>
                Decline
            </Button>
            <Input
                className="min-w-0 flex-1"
                disabled={!canInteract}
                placeholder="Suggest alternative…"
                type="text"
                value={draftSuggestion}
                onChange={(event) => onDraftSuggestionChange(event.target.value)}
                onKeyDown={(event) => submitSuggestionFromKey(event, draftSuggestion, onSuggest)}
            />
        </ChangeProposalCard.Actions>
    );
};

const renderProposalBody = (proposal: ChangeProposal): ReactNode => {
    if (proposal.type === 'update_tasks' && is.nonEmptyArray(proposal.tasks)) {
        return <ChangeProposalCard.TaskList tasks={proposal.tasks} />;
    }
    if (proposal.type === 'update_title') {
        return <ChangeProposalCard.TitleChange current={proposal.current} proposed={proposal.proposed} />;
    }
    if (proposal.type === 'add_skill' || proposal.type === 'remove_skill' || proposal.type === 'adjust_skill') {
        return (
            <ChangeProposalCard.SkillEntry
                title={proposal.proposed}
                subtitle="Hard skill"
                struck={proposal.type === 'remove_skill'}
            />
        );
    }
    return <ChangeProposalCard.Text>{proposal.proposed}</ChangeProposalCard.Text>;
};

const ChangeProposalInline: FunctionComponent<Props> = ({ event, interactive }) => {
    const callback = useSubmitChatInteractionCallback();
    const [draftSuggestion, setDraftSuggestion] = useState('');

    const { proposal, blockId, resolution } = event.payload;
    const status: ChangeProposalStatus = resolution?.status ?? 'pending';
    const suggestion = resolution?.status === 'suggested' ? resolution.suggestion : undefined;

    const decide = (accepted: boolean): void => {
        callback.mutate({
            blockId,
            eventId: event.eventId,
            payload: { actions: [{ accepted, actionId: proposal.id, type: 'decision' }] },
        });
    };

    const suggest = (input: string): void => {
        setDraftSuggestion(input);
        callback.mutate({
            blockId,
            eventId: event.eventId,
            payload: { actions: [{ actionId: proposal.id, input, type: 'input' }] },
        });
    };

    return (
        <AssistantBubble>
            <AssistantBubble.Extras>
                <ChangeProposalCard className="ml-1">
                    <ChangeProposalCard.Header label={proposal.label} />
                    <ChangeProposalCard.Body>{renderProposalBody(proposal)}</ChangeProposalCard.Body>
                    <ProposalFooterActions
                        callbackPending={callback.isPending}
                        draftSuggestion={draftSuggestion}
                        interactive={interactive}
                        onAccept={() => decide(true)}
                        onDecline={() => decide(false)}
                        onDraftSuggestionChange={setDraftSuggestion}
                        onSuggest={suggest}
                        status={status}
                        suggestion={suggestion}
                    />
                </ChangeProposalCard>
            </AssistantBubble.Extras>
        </AssistantBubble>
    );
};

export const changeProposalRenderer: StructuredRenderer<ChangeProposalResponseEvent> = {
    renderInline: (event, context) => <ChangeProposalInline event={event} interactive={context.interactive} />,
    toolId: PRESENT_CHANGE_PROPOSAL_TOOL_ID,
};
